import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function Terrasa() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Летняя терраса на крыше в Петербурге — Паруса на крыше",
    description: "Открытая летняя терраса ресторана на крыше в центре Санкт-Петербурга. Панорамный вид 300° на город, свежий воздух, живая музыка. Ул. Марата, 5, 6 этаж.",
    keywords: "летняя терраса ресторан Петербург, открытая терраса ресторан СПб, ресторан с террасой Петербург, терраса на крыше СПб, ресторан на открытом воздухе Петербург, веранда ресторан СПб",
    image: "/photos/terrace.jpg",
    canonical: "/terrasa",
  });
  useBreadcrumbs([{ name: "Летняя терраса", path: "/terrasa" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">

      {/* Hero */}
      <section className="relative h-[90vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/terrace.webp"
            alt="Открытая летняя терраса ресторана Паруса на крыше — панорама Петербурга"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.28) saturate(0.8)", transform: "scale(1.05)" }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,13,9,0.99) 0%, rgba(5,20,10,0.65) 50%, rgba(5,20,10,0.3) 100%)" }} />
        </div>
        <div
          className="relative z-10 text-center px-6 max-w-3xl mx-auto"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 0.3s" }}
        >
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Открытый воздух · 6 этаж</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            Летняя терраса<br /><span className="text-accent italic">на крыше</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-12 max-w-xl mx-auto leading-relaxed">
            Открытая терраса на крыше — 300° панорамы, закаты над городом, живая музыка и свежий воздух с мая по сентябрь
          </p>
          <button
            onClick={openBookingModal}
            className="bg-primary/20 border border-primary text-accent px-14 py-5 text-[10px] tracking-[0.35em] uppercase font-medium hover:bg-primary hover:text-background transition-colors"
          >
            Забронировать место на террасе
          </button>
        </div>
      </section>

      {/* О террасе */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Летняя терраса</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                Петербург у вас<br /><span className="text-accent italic">перед глазами</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                Открытая терраса «Парусов» расположена на 6-м этаже Olympic Plaza — это не летний бар с пластиковыми стульями, а полноценный ресторан под открытым небом. Те же блюда авторской кухни, тот же сервис, те же живые выступления — только с ветром с Невы и панорамой на 360 метров вокруг.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10">
                Вид с террасы — это Казанский собор прямо перед вами, крыши Центрального района, Исаакиевский собор на горизонте и закатное солнце, которое каждый вечер устраивает спектакль над городом.
              </p>
              <button
                onClick={openBookingModal}
                className="border border-primary/40 text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary transition-colors"
              >
                Выбрать столик →
              </button>
            </div>
            <div>
              <img
                src="/photos/view_summer.webp"
                alt="Летний вид с открытой террасы ресторана Паруса на крыше"
                className="w-full aspect-video object-cover border border-primary/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Особенности */}
      <section className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Почему терраса</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Шесть причин выйти на воздух</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "✦", title: "Панорама 300°", desc: "Казанский собор, Исаакий, Невский проспект — весь исторический центр Петербурга как на ладони с высоты 6-го этажа" },
              { icon: "✦", title: "Живая музыка", desc: "Джазовые вечера и лаунж-выступления под открытым небом — расписание публикуется в разделе «Афиша»" },
              { icon: "✦", title: "Авторская кухня", desc: "Полное меню ресторана на открытом воздухе — от лёгких летних закусок до горячих блюд из хоспера" },
              { icon: "✦", title: "Белые ночи", desc: "С мая по август терраса работает до 01:00 в пятницу и субботу — ночной Петербург с крыши не имеет аналогов" },
              { icon: "✦", title: "Закаты над городом", desc: "Летом солнце садится около 22:00–22:30 — бронируйте на это время, чтобы застать золотой час над крышами" },
              { icon: "✦", title: "Свежий воздух", desc: "Тёплый летний вечер, лёгкий ветер с Невы и кружка холодного лимонада — этого не воспроизвести в закрытом зале" },
            ].map((item) => (
              <div key={item.title} className="group p-6 border border-primary/15 hover:border-primary/40 transition-colors">
                <div className="text-primary/50 mb-4 text-sm">{item.icon}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-3">{item.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Сезон */}
      <section className="py-28 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Сезон и режим работы</div>
              <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-8 leading-tight">
                Терраса открыта<br />с <span className="text-accent italic">мая по сентябрь</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-8">
                При хорошей погоде терраса работает уже с конца апреля. В дождь или холод — тёплый зал с панорамными окнами от пола до потолка открывает тот же вид, только в комфорте. Уточняйте наличие мест на террасе при бронировании.
              </p>
              <div className="space-y-3">
                {[
                  { season: "Май — август", note: "Белые ночи, закаты в 22:30, работаем до 01:00 пт–сб" },
                  { season: "Сентябрь", note: "Бархатный сезон, вечерние огни города, прохладные вечера" },
                  { season: "Закрытый зал", note: "Та же панорама в тепле — круглый год, в любую погоду" },
                ].map((item) => (
                  <div key={item.season} className="flex items-start gap-4 py-4 border-b border-primary/10 last:border-0">
                    <span className="text-primary/40 shrink-0 mt-0.5">✦</span>
                    <div>
                      <div className="text-accent text-sm font-light mb-1">{item.season}</div>
                      <div className="text-muted-foreground text-xs font-light">{item.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="/photos/view_clouds.webp"
                alt="Вид с террасы ресторана Паруса на крыше в Петербурге"
                className="w-full aspect-square object-cover border border-primary/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Лучшее время */}
      <section className="py-24 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <div className="mb-12 text-center">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Советы</div>
            <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground">Как выбрать лучший столик</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: "01", title: "Просите место на террасе", body: "При бронировании сразу уточните, что хотите место на открытой террасе. Лучшие столики у края разбирают первыми." },
              { num: "02", title: "Бронируйте на закат", body: "Летом бронируйте на 20:30–21:30 — именно в это время солнце садится прямо за горизонтом. Зрелище незабываемое." },
              { num: "03", title: "Выходные — заранее", body: "В пятницу и субботу в летний сезон места на террасе заполняются за 3–5 дней. Бронируйте заранее, особенно для компании." },
            ].map((item) => (
              <div key={item.num} className="p-8 border border-primary/15 hover:border-primary/30 transition-colors">
                <div className="text-[40px] font-serif font-light text-primary/15 mb-4 leading-none">{item.num}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-3">{item.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Режим работы */}
      <section className="py-20 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-serif font-light text-foreground mb-8">Режим работы</h2>
          <div className="flex flex-wrap justify-center gap-4 text-[10px] uppercase tracking-widest text-muted-foreground/60">
            <span className="border border-primary/20 px-5 py-3">Пн–Чт · 12:00–23:00</span>
            <span className="border border-primary/20 px-5 py-3">Пт–Сб · 12:00–01:00</span>
            <span className="border border-primary/20 px-5 py-3">Вс · 12:00–23:00</span>
          </div>
          <p className="text-muted-foreground/50 text-xs font-light mt-6">
            Терраса работает при хорошей погоде · май – сентябрь · уточняйте при бронировании
          </p>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/terrasa"]} title="Вопросы о террасе" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl font-serif font-light text-foreground mb-6">Займите место на террасе</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Лучший летний ужин в Петербурге — на открытом воздухе с видом на весь город. Бронируйте заранее, места ограничены.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openBookingModal}
              className="bg-primary border border-primary text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors"
            >
              Забронировать столик
            </button>
            <a
              href="tel:+78129911090"
              className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors"
            >
              +7 (812) 991-10-90
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
